import { css, cx } from "@emotion/css";
import type { FC } from "react";
import { useContext, useState, useCallback, useEffect } from "react";

import {
  Box,
  Chip,
  Collapse,
  Colors,
  Icons,
  Tooltip,
  tooltipClasses,
  Typography,
  useMediaQuery,
} from "@analyticsMD/quil";
import {
  styles as commonStyles,
  sxStyles as commonSxStyles,
} from "./common-styles";
import APIContext from "../../contexts/APIContext";
import InsightItemNote from "../insights/insight-item-note";
import NotificationToast from "../notification-toast";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Insights from "@mui/icons-material/Insights";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import CheckIcon from "@mui/icons-material/Check";
import PatientInsightsQuote from "../../types/PatientInsightsQuote";
import { grey } from "@mui/material/colors";
import PatientInsights from "../../types/PatientInsights";

const TOAST_MESSAGE = "Thanks for sharing your feedback";

const ERROR_TOAST_MESSAGE =
  "There was an error submitting your feedback. Please try again.";

const styles = {
  ...commonStyles,
  insightsSection: (useWideLayout) => css`
    background-color: ${Colors.white};
    padding-bottom: ${useWideLayout ? "1.25rem" : "1rem"};
    padding-right: "0.75rem";
  `,
  selector: css`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 0.625rem;
    align-items: flex-end;
    margin-top: -0.75rem;
    justify-content: flex-start;
    padding-left: 2.6rem;
  `,
  suggestion: css`
    padding-left: 3rem;
  `,
  headerIcon: css`
    margin-right: 0.5rem;
    color: ${Colors.good_80};
    height: 1.25rem;
    width: 1.25rem;
  `,
  infoIcon: css`
    margin-left: 0.5rem;
    color: ${Colors.grey_25};
  `,
  expandCollapseIcon: css`
    color: ${Colors.grey_40};
  `,
};

const sxStyles = {
  ...commonSxStyles,
  insightsIcon: {
    marginRight: "0.5rem",
    borderRadius: "0.25rem",
    color: Colors.white,
    backgroundColor: Colors.good_80,
    padding: "0.2rem",
  },
  filterChipSection: {
    paddingTop: "0.15rem",
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  filterChip: {
    fontSize: "0.7rem",
    marginRight: "0.5rem",
  },
  filterChipContent: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  filterChipContentCheckIcon: {
    width: "1rem",
    height: "1rem",
    marginLeft: "0.25rem",
    color: grey[600],
  },
};

const InsightsSection: FC = () => {
  const { patientInsightsQuery } = useContext(APIContext);
  const [showSection, setShowSection] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showErrorToast, setShowErrorToast] = useState(false);
  const [insightTypeFilter, setInsightTypeFilter] = useState<string[]>([]);
  const [insights, setInsights] = useState<PatientInsights | null>(null);

  const filteredQuotes = useCallback(
    (quotes: PatientInsightsQuote[]) => {
      return insightTypeFilter.length > 0
        ? quotes.filter((q) => insightTypeFilter.includes(q.insight_type))
        : quotes;
    },
    [insightTypeFilter]
  );

  const renderInsightsFilters = () => {
    if (!insights) {
      return null;
    }
    // Get the count of each insight type
    const insightTypeCounts = insights?.notes
      .flatMap((n) => n.quotes.map((q) => q.insight_type))
      .reduce((map, type) => {
        map.set(type, (map.get(type) || 0) + 1);
        return map;
      }, new Map());

    const isInsightTypeFiltered = (type: string) => {
      return insightTypeFilter.includes(type);
    };

    const handleInsightTypeFilter = (type: string) => {
      if (isInsightTypeFiltered(type)) {
        setInsightTypeFilter(insightTypeFilter.filter((t) => t !== type));
      } else {
        setInsightTypeFilter([...insightTypeFilter, type]);
      }
      setShowSection(true);
    };
    return (
      <Box sx={sxStyles.filterChipSection}>
        {Array.from(insightTypeCounts.entries()).map(
          ([type, count]: [string, number]) => (
            <Chip
              key={type}
              label={
                <Box sx={sxStyles.filterChipContent}>
                  <span>
                    {type} ({count})
                  </span>
                  {isInsightTypeFiltered(type) && (
                    <CheckIcon sx={sxStyles.filterChipContentCheckIcon} />
                  )}
                </Box>
              }
              variant={isInsightTypeFiltered(type) ? "filled" : "outlined"}
              sx={sxStyles.filterChip}
              onClick={() => handleInsightTypeFilter(type)}
            />
          )
        )}
      </Box>
    );
  };

  const renderInsightsSection = () => {
    if (insights) {
      const notes = insights.notes
        .sort((a, b) => a.id - b.id)
        .map((n, index) => {
          const filteredNoteQuotes = filteredQuotes(n.quotes);
          if (filteredNoteQuotes.length > 0) {
            return (
              <Box key={index}>
                <InsightItemNote
                  note={n}
                  setShowToast={setShowToast}
                  setShowErrorToast={setShowErrorToast}
                  insightTypeFilter={insightTypeFilter}
                />
              </Box>
            );
          } else {
            return null;
          }
        });

      return (
        <Box sx={{ marginTop: "0.6rem" }}>
          {notes}
          {renderInsightsFilters()}
        </Box>
      );
    } else {
      return (
        <div className={styles.emptyWrapper}>
          <Icons.PlaceholderComplete className={styles.emptyImg} />
          <span className={styles.emptyMsg}>
            There are currently no patient insights to display
          </span>
        </div>
      );
    }
  };

  const useWideLayout = useMediaQuery("(min-width:62.5rem)");

  const tooltipContent = (
    <Typography sx={{ fontSize: "0.875rem" }}>
      Key insights from provider and therapy notes. Updated daily at 7:00 am.
    </Typography>
  );

  const insightsTooltip = (
    <div>
      <Tooltip
        PopperProps={{
          disablePortal: true,
          sx: {
            [`& .${tooltipClasses.tooltip}`]: {
              width: "12rem",
              padding: "0.8rem",
            },
            [`& .${tooltipClasses.arrow}`]: {
              fontSize: "large",
            },
          },
        }}
        disableFocusListener
        title={tooltipContent}
        arrow
        placement="top-end"
      >
        <Box sx={{ display: "flex" }}>
          <InfoOutlined
            fontSize="small"
            className={cx(styles.headerIcon, styles.infoIcon)}
          />
        </Box>
      </Tooltip>
    </div>
  );

  useEffect(() => {
    if (useWideLayout) {
      setShowSection(true);
    }
  }, [useWideLayout]);

  useEffect(() => {
    if (patientInsightsQuery.data) {
      setInsights(patientInsightsQuery.data);
    }
  }, [patientInsightsQuery.data]);

  return (
    <>
      <Box
        className={cx(
          styles.roundedSection,
          styles.insightsSection(useWideLayout)
        )}
      >
        <Box
          sx={sxStyles.headingWrapper}
          onClick={() => setShowSection(!showSection)}
        >
          <Box sx={sxStyles.sectionHeading}>
            <Insights fontSize="inherit" sx={sxStyles.insightsIcon} />
            <span>Key insights</span>
            {insightsTooltip}
          </Box>
          <>
            {!showSection && (
              <ExpandMoreIcon className={styles.expandCollapseIcon} />
            )}
            {showSection && (
              <ExpandLessIcon className={styles.expandCollapseIcon} />
            )}
          </>
        </Box>
        <Collapse in={showSection} unmountOnExit>
          {renderInsightsSection()}
        </Collapse>
        {!showSection && (
          <Box sx={{ marginTop: "0.8rem" }}>{renderInsightsFilters()}</Box>
        )}
        <NotificationToast
          message={TOAST_MESSAGE}
          showToast={showToast}
          isError={false}
          autoHideDuration={2000}
          onClose={() => setShowToast(false)}
        />
        <NotificationToast
          message={ERROR_TOAST_MESSAGE}
          showToast={showErrorToast}
          isError
          autoHideDuration={2000}
          onClose={() => setShowErrorToast(false)}
        />
      </Box>
    </>
  );
};

export default InsightsSection;
